import { useMemo, useState } from 'react';
import { runAudit } from '../services/auth';

/**
 * NewAudit - Paste extracted invoice fields, run policy evaluation +
 * recommendation against the fixed `afip-invoice-audit` pack, and hand the
 * resulting trace_id back to the caller via `onComplete`.
 *
 * Two-step layout consistent with the classify/extract family: a step-1
 * card for the input JSON, a step-2 card to launch the run. Set `hideHero`
 * when embedding this form on a page that already renders its own hero
 * (e.g. the unified home screen).
 */

const TENANT_PACK = 'afip-invoice-audit';

// The 16 fields the AFIP invoice pack expects. Missing fields are legal —
// policies answer insufficient_data for them — so this only warns.
const EXPECTED_FIELDS = [
  'ejercicio',
  'moneda',
  'fecha_factura',
  'tipo_cambio',
  'importe_neto',
  'impuestos_iva',
  'impuestos_otros',
  'impuestos',
  'importe_bruto',
  'cuit_emisor',
  'razon_social_emisor',
  'cuit_receptor',
  'razon_social_receptor',
  'punto_de_venta',
  'tipo_de_comprobante',
  'numero_de_comprobante',
];

const SparklesIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3l1.912 5.813a2 2 0 001.275 1.275L21 12l-5.813 1.912a2 2 0 00-1.275 1.275L12 21l-1.912-5.813a2 2 0 00-1.275-1.275L3 12l5.813-1.912a2 2 0 001.275-1.275L12 3z" />
  </svg>
);

function parseInvoiceJson(text) {
  if (!text.trim()) {
    return { valid: false, missing: [] };
  }

  let data;
  try {
    data = JSON.parse(text);
  } catch {
    return { valid: false, error: 'Invalid JSON — check for missing commas, quotes or brackets.', missing: [] };
  }

  const isPlainObject = data !== null && typeof data === 'object' && !Array.isArray(data);
  if (!isPlainObject) {
    return { valid: false, error: 'The pasted JSON must be a plain object, not an array or primitive.', missing: [] };
  }

  const missing = EXPECTED_FIELDS.filter((field) => !(field in data));
  return { valid: true, data, missing };
}

export function NewAudit({ onComplete, hideHero }) {
  const [jsonText, setJsonText] = useState('');
  const [fileName, setFileName] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);

  const parsed = useMemo(() => parseInvoiceJson(jsonText), [jsonText]);

  function handleFileLoad(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setJsonText(String(reader.result || ''));
      setError(null);
    };
    reader.onerror = () => setError('Could not read the selected file.');
    reader.readAsText(file);

    setFileName(file.name);
    e.target.value = ''; // allow re-selecting the same file
  }

  async function handleRunAudit() {
    setError(null);

    if (!jsonText.trim()) {
      setError('Paste the extracted invoice JSON before running the audit.');
      return;
    }
    if (!parsed.valid) {
      setError(parsed.error || 'The pasted content is not valid JSON.');
      return;
    }

    setRunning(true);
    try {
      const { trace_id } = await runAudit(parsed.data);
      onComplete(trace_id);
    } catch (err) {
      setError(err.message || 'Error running the audit.');
    } finally {
      setRunning(false);
    }
  }

  return (
    <section className="detail">
      {!hideHero && (
        <div className="hero">
          <div className="powered-badge">
            <SparklesIcon />
            <span>Powered by AI</span>
          </div>
          <h1>New <span>Audit</span></h1>
          <p>Paste the extracted invoice fields, evaluate policies and get a recommendation</p>
        </div>
      )}

      <div className="invoice-page">
        <div className="upload-section">
          <div className="section-header">
            <div className="step-number">1</div>
            <span className="section-title">Invoice data</span>
          </div>

          <div className="card">
            <div className="new-audit-card-body">
              <textarea
                className="json-input mono"
                rows={14}
                placeholder='{"cuit_emisor": "20-12345678-9", "importe_bruto": 1000.00, ...}'
                value={jsonText}
                onChange={(e) => {
                  setJsonText(e.target.value);
                  setError(null);
                }}
                disabled={running}
                spellCheck={false}
              />

              <div className="file-load-row">
                <label className="btn-secondary file-load-label">
                  Load from file
                  <input
                    type="file"
                    accept="application/json,.json"
                    onChange={handleFileLoad}
                    disabled={running}
                  />
                </label>
                {fileName && <span className="file-load-name mono">{fileName}</span>}
              </div>

              {jsonText.trim() && parsed.valid && parsed.missing.length > 0 && (
                <div className="degraded-panel">
                  Missing fields (policies will answer "insufficient data" for these):{' '}
                  <span className="mono">{parsed.missing.join(', ')}</span>
                </div>
              )}

              {jsonText.trim() && !parsed.valid && parsed.error && (
                <div className="error-message">{parsed.error}</div>
              )}
            </div>
          </div>
        </div>

        <div className="result-section">
          <div className="section-header">
            <div className="step-number">2</div>
            <span className="section-title">Run audit</span>
          </div>

          <div className="card">
            <div className="new-audit-card-body">
              <div className="form-group">
                <label>Tenant pack</label>
                <input type="text" value={TENANT_PACK} readOnly disabled />
              </div>

              <button
                className="btn-primary"
                onClick={handleRunAudit}
                disabled={running || !jsonText.trim()}
              >
                {running ? (
                  <>
                    <span className="spinner"></span>
                    Running audit…
                  </>
                ) : (
                  'Run Audit'
                )}
              </button>

              {error && <div className="error-message">{error}</div>}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
